/**
 * 官方模型服务端适配器。
 *
 * 与用户自行配置的 Provider 有意分开：请求只发往本站 API，认证依赖
 * HttpOnly cookie，不读取或发送浏览器里的第三方 API Key。
 */
import {
  TimeoutGuard,
  jsonHeaders,
  readJsonBody,
  readSSEData,
  resolveDeps,
  type ResolvedDeps,
} from './http';
import { buildChatBody, mapCompletionResponse, mapStreamPayload } from './openai';
import type {
  ChatRequest,
  ProviderAdapter,
  ProviderDeps,
  ProviderErrorCode,
  ResolvedProviderConfig,
  ServerProviderErrorCode,
  StreamChunk,
  StreamUsage,
} from './types';
import { SERVER_PROVIDER_ID } from './ids';

export const SERVER_KIND = 'custom' as const;
export { SERVER_PROVIDER_ID };

function apiBaseUrl(config: ResolvedProviderConfig): string {
  const trimmed = config.baseUrl.trim();
  const value = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim().replace(/\/+$/, '');
  const deploymentBase = value === undefined || value === '' ? '/api' : value;
  const configured = trimmed.length > 0 ? trimmed : `${deploymentBase}/v1`;
  return configured.replace(/\/+$/, '');
}

export function serverModelsUrl(config: ResolvedProviderConfig): string {
  return `${apiBaseUrl(config)}/models`;
}

export function serverChatCompletionsUrl(config: ResolvedProviderConfig): string {
  return `${apiBaseUrl(config)}/chat/completions`;
}

interface ServerErrorPayload {
  code?: string;
  message?: string;
  error?: { code?: string; message?: string } | string;
}

function normalizeServerError(payload: unknown, status?: number): { message: string; code: ProviderErrorCode; serverCode?: ServerProviderErrorCode; status?: number } {
  const value = (payload ?? {}) as ServerErrorPayload;
  const nested = typeof value.error === 'object' && value.error !== null ? value.error : undefined;
  const rawCode = String(nested?.code ?? value.code ?? '').toLowerCase();
  const rawMessage = typeof value.error === 'string' ? value.error : nested?.message ?? value.message;
  let serverCode: ServerProviderErrorCode | undefined;
  if (status === 401 || rawCode.includes('unauthorized') || rawCode.includes('auth')) serverCode = 'unauthorized';
  else if (status === 402 || rawCode.includes('balance') || rawCode.includes('insufficient')) serverCode = 'insufficient_balance';
  else if (status === 429 || rawCode.includes('rate') || rawCode.includes('too_many')) serverCode = 'rate_limited';
  else if (status === 503 || rawCode.includes('service_unavailable') || rawCode.includes('upstream_unavailable')) serverCode = 'service_unavailable';
  else if (rawCode.includes('unavailable') || rawCode.includes('not_found') || rawCode.includes('model')) serverCode = 'model_unavailable';
  else if (rawCode.includes('billing') || rawCode.includes('charge') || rawCode.includes('meter')) serverCode = 'billing_error';
  const message = rawMessage ?? `官方模型请求失败${status ? `（HTTP ${status}）` : ''}`;
  return { message, code: 'http', ...(serverCode === undefined ? {} : { serverCode }), ...(status === undefined ? {} : { status }) };
}

function directServerChunk(payload: unknown): StreamChunk[] | null {
  if (!payload || typeof payload !== 'object') return null;
  const value = payload as { type?: unknown; delta?: unknown; error?: unknown; usage?: unknown };
  if (typeof value.type !== 'string') return null;
  if (value.type === 'text' || value.type === 'reasoning' || value.type === 'tool_call') {
    return [{ type: value.type, ...(typeof value.delta === 'string' ? { delta: value.delta } : {}) }];
  }
  if (value.type === 'done') {
    return [{ type: 'done', ...(value.usage && typeof value.usage === 'object' ? { usage: value.usage as StreamUsage } : {}) }];
  }
  if (value.type === 'error') {
    const normalized = normalizeServerError(value.error ?? value);
    return [{ type: 'error', error: normalized.message, code: normalized.code, ...(normalized.serverCode === undefined ? {} : { serverCode: normalized.serverCode }), ...(normalized.status === undefined ? {} : { status: normalized.status }) }];
  }
  return null;
}

function parseModels(payload: unknown): string[] {
  const value = payload as { data?: unknown; models?: unknown } | null;
  const raw = Array.isArray(value?.data) ? value.data : Array.isArray(value?.models) ? value.models : Array.isArray(payload) ? payload : [];
  return raw.flatMap((item) => {
    if (typeof item === 'string' && item.trim()) return [item];
    if (!item || typeof item !== 'object') return [];
    const model = item as { id?: unknown; slug?: unknown; name?: unknown; displayName?: unknown; isPublished?: unknown };
    const id = typeof model.slug === 'string' ? model.slug : typeof model.id === 'string' ? model.id : typeof model.name === 'string' ? model.name : '';
    if (!id.trim()) return [];
    const label = typeof model.displayName === 'string' && model.displayName.trim() ? model.displayName.trim() : id;
    return [label === id ? id : `${label} (${id})`];
  });
}

export class ServerBackedAdapter implements ProviderAdapter {
  readonly id: string;
  readonly kind = SERVER_KIND;
  private readonly config: ResolvedProviderConfig;
  private readonly deps: ResolvedDeps;

  constructor(config: ResolvedProviderConfig, deps?: ProviderDeps) {
    this.config = config;
    this.id = config.id;
    this.deps = resolveDeps(deps);
  }

  private async request(url: string, init: RequestInit, externalSignal?: AbortSignal): Promise<{ response: Response; guard: TimeoutGuard }> {
    const guard = new TimeoutGuard(this.config.timeoutMs ?? this.deps.timeoutMs, externalSignal);
    try {
      const response = await this.deps.fetch(url, { ...init, signal: guard.signal, credentials: 'include' });
      if (!response.ok) {
        let payload: unknown;
        try {
          payload = await response.json();
        } catch {
          payload = undefined;
        }
        const normalized = normalizeServerError(payload, response.status);
        throw Object.assign(new Error(normalized.message), normalized);
      }
      return { response, guard };
    } catch (error) {
      guard.dispose();
      throw error;
    }
  }

  async listModels(): Promise<string[]> {
    const { response, guard } = await this.request(
      serverModelsUrl(this.config),
      { method: 'GET', headers: jsonHeaders(undefined, this.config.headers) },
    );
    try {
      return parseModels(await readJsonBody(response));
    } finally {
      guard.dispose();
    }
  }

  async *chat(req: ChatRequest): AsyncIterable<StreamChunk> {
    if (req.signal?.aborted) return;
    let opened: { response: Response; guard: TimeoutGuard };
    try {
      opened = await this.request(
        serverChatCompletionsUrl(this.config),
        {
          method: 'POST',
          headers: jsonHeaders(undefined, { ...(this.config.headers ?? {}), Accept: 'text/event-stream' }),
          body: JSON.stringify({ ...buildChatBody(req), ...(req.intimacy ? { intimacy: req.intimacy } : {}) }),
        },
        req.signal,
      );
    } catch (error) {
      if (req.signal?.aborted) return;
      const normalized = normalizeServerError({ message: error instanceof Error ? error.message : '官方模型请求失败' }, (error as { status?: number }).status);
      yield { type: 'error', error: normalized.message, code: normalized.code, ...(normalized.serverCode === undefined ? {} : { serverCode: normalized.serverCode }), ...(normalized.status === undefined ? {} : { status: normalized.status }) };
      return;
    }

    const { response, guard } = opened;
    try {
      if (!req.settings.stream) {
        for (const chunk of mapCompletionResponse(await readJsonBody(response))) yield chunk;
        return;
      }
      if (!response.body) {
        yield { type: 'error', error: '官方模型响应没有可读流', code: 'parse' };
        return;
      }
      let usage: StreamUsage | undefined;
      for await (const payload of readSSEData(response.body, guard.signal)) {
        if (req.signal?.aborted) return;
        guard.reset();
        if (payload.trim() === '[DONE]') break;
        let json: unknown;
        try {
          json = JSON.parse(payload);
        } catch {
          yield { type: 'error', error: '官方模型返回了无法解析的 SSE 数据', code: 'parse' };
          return;
        }
        const direct = directServerChunk(json);
        if (direct !== null) {
          for (const chunk of direct) {
            if (chunk.usage) usage = chunk.usage;
            yield chunk;
          }
          continue;
        }
        const mapped = mapStreamPayload(payload);
        if (mapped.usage) usage = mapped.usage;
        for (const chunk of mapped.chunks) yield chunk;
      }
      if (!req.signal?.aborted) yield usage ? { type: 'done', usage } : { type: 'done' };
    } catch (error) {
      if (!req.signal?.aborted) {
        const normalized = normalizeServerError({ message: error instanceof Error ? error.message : '官方模型请求失败' }, (error as { status?: number }).status);
        yield { type: 'error', error: normalized.message, code: normalized.code, ...(normalized.serverCode === undefined ? {} : { serverCode: normalized.serverCode }), ...(normalized.status === undefined ? {} : { status: normalized.status }) };
      }
    } finally {
      guard.abort();
      guard.dispose();
    }
  }
}

export function createServerBackedAdapter(config: ResolvedProviderConfig, deps?: ProviderDeps): ProviderAdapter {
  return new ServerBackedAdapter(config, deps);
}
